/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EventIcon from '@material-ui/icons/Event';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import StorefrontIcon from '@material-ui/icons/Storefront';
import chroma from 'chroma-js';
import React, { useState } from 'react';
import { PublicShopSetting } from '../../../@interfaces/shop-setting';
import { TicketUsageLogWithTicketInfo } from '../../../@interfaces/ticket/ticket-usage-log';
import { cancelReservation } from '../../../api/use-reservation';
import { PublicWorkspaceCustomerReservation } from '../../../api/use-workspace-customer-reservations';
import { ModalMenu } from '../../../ui-library/DropdownMenu/ModalMenu';
import { MenuOption } from '../../../ui-library/DropdownMenu/type';
import { List } from '../../../ui-library/List/List';
import { ListItem } from '../../../ui-library/List/ListItem';
import {
  StatusColorType,
  StatusLabel,
} from '../../../ui-library/StatusLabel/StatusLabel';
import { CancelComplete } from './CancelComplete';
import { CancelDialog } from './CancelDialog';
import { MyPageTicket } from './types';

type TabType = 'reservation' | 'ticket-usage' | 'ticket-purchase';

type UsageStatusType = 'used' | 'refunded' | 'expired' | 'issued';

type TicketStatusType = 'active' | 'expired' | 'near_expiry';

type MyPageTopPageProps = {
  reservations: PublicWorkspaceCustomerReservation[];
  workspaceUid: string;
  lineSessionKey?: string;
  tickets?: MyPageTicket[];
  usageLogs?: TicketUsageLogWithTicketInfo[];
  primaryColor: string;
  onReservationCanceled?: (reservationKey: string) => void;
  shopSetting?: PublicShopSetting;
};

// 曜日の定数
const DAYS_OF_WEEK = ['日', '月', '火', '水', '木', '金', '土'] as const;

// 日付フォーマット用のヘルパー関数
const formatDateFromISO = (isoDate: string): string => {
  return isoDate.split('T')[0].replace(/-/g, '/');
};

// 日付文字列のフォーマット（nullableな日付用）
const formatDate = (date: string | null | undefined): string => {
  if (!date) return '';
  return date.replace(/-/g, '/');
};

// 予約リマインダーラベルの定数
const REMINDER_LABELS = {
  TODAY: '今日',
  SOON: 'もうすぐ',
} as const;

// 利用ステータスの定数
const USAGE_STATUS_CONFIG = {
  used: { label: '利用', color: 'success' },
  refunded: { label: '利用取消', color: 'warning' },
  expired: { label: '期限切れ', color: 'disabled' },
  issued: { label: '新規発行', color: 'disabled' },
} as const;

// デフォルト値の定数
const DEFAULT_TICKET_VALUES = {
  TICKET_PLAN_NAME: 'チケット',
  SHOP_NAME: '不明',
  UNIT_SUFFIX: '枚',
} as const;

// チケットステータスラベルの定数
const TICKET_STATUS_LABELS = {
  NEAR_EXPIRY: '期限切れ間近',
  EXPIRED: '期限切れ',
} as const;

// チケット購入履歴の型定義
type TicketPurchaseHistoryItem = {
  id: string;
  purchaseDate: string;
  shopName: string;
  ticketPlanName: string;
  totalCount: number;
  unitSuffix: string;
  price: number;
  status: TicketStatusType;
  remainingCount: number;
  expireDate: string;
};

// モックデータの型定義
type TicketUtilizeHistory = {
  id: string;
  dateOfUse: string;
  ticketTitle: string;
  expireDate: string;
  usageStatus: UsageStatusType;
  changeAmount: number;
  unitType: string;
  newRemaining: number | null;
  prevRemaining: number | null;
  ticketId: number;
};

// 曜日を取得する関数
const getDayOfWeek = (dateString: string): string => {
  const date = new Date(dateString);
  return DAYS_OF_WEEK[date.getDay()];
};

// 日付と曜日をフォーマットする関数
const formatDateWithWeekday = (date: string, time: string): string => {
  const dayOfWeek = getDayOfWeek(date);
  return `${date}(${dayOfWeek}) ${time}`;
};

// 予約日に応じたラベルを取得する関数
const getReservationReminderLabel = (
  dateString: string
): { label: string; show: boolean; color: StatusColorType } => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const reservationDate = new Date(dateString);
  reservationDate.setHours(0, 0, 0, 0);

  const diffTime = reservationDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return { label: REMINDER_LABELS.TODAY, show: true, color: 'alert' };
  } else if (diffDays > 0 && diffDays <= 5) {
    const remainingLabel = `あと${diffDays}日`;
    return { label: remainingLabel, show: true, color: 'warning' };
  }

  return { label: '', show: false, color: 'warning' };
};

// ステータス設定の取得関数
const getUsageStatusConfig = (
  usageStatus: UsageStatusType
): { label: string; color: StatusColorType } => {
  const config = USAGE_STATUS_CONFIG[usageStatus];
  return config || { label: '', color: 'disabled' };
};

// チケット購入履歴のステータスラベルを取得する関数
const getTicketStatusLabel = (
  status: TicketStatusType
): { label: string; color: StatusColorType } | null => {
  switch (status) {
    case 'near_expiry':
      return { label: TICKET_STATUS_LABELS.NEAR_EXPIRY, color: 'alert' };
    case 'expired':
      return { label: TICKET_STATUS_LABELS.EXPIRED, color: 'disabled' };
    default:
      return null;
  }
};

const parseReservationDateTime = (
  reservation: PublicWorkspaceCustomerReservation
): Date | null => {
  const { date, time } = reservation;
  if (!date || !time) {
    return null;
  }

  const normalizedTime = (() => {
    if (/^\d{2}:\d{2}$/.test(time)) {
      return `${time}:00`;
    }
    if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
      return time;
    }
    return null;
  })();

  if (!normalizedTime) {
    return null;
  }

  const candidates = [`${date}T${normalizedTime}`, `${date} ${normalizedTime}`];

  for (const candidate of candidates) {
    const dateTime = new Date(candidate);
    if (!Number.isNaN(dateTime.getTime())) {
      return dateTime;
    }
  }

  return null;
};

export const MyPageTopPage = ({
  reservations,
  workspaceUid,
  lineSessionKey,
  tickets,
  usageLogs = [],
  primaryColor,
  onReservationCanceled,
  shopSetting,
}: MyPageTopPageProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState<TabType>('reservation');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelCompleteOpen, setCancelCompleteOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] =
    useState<PublicWorkspaceCustomerReservation | null>(null);

  // チケット購入履歴データの生成
  const ticketPurchaseHistory: TicketPurchaseHistoryItem[] =
    React.useMemo(() => {
      if (!usageLogs || usageLogs.length === 0) return [];

      // チケット発行（購入）のログのみをフィルタリング
      const issueLogsMap = new Map<number, TicketUsageLogWithTicketInfo>();

      usageLogs.forEach((log) => {
        if (log.changeType === 'issue') {
          // 同じチケットIDで最も古い発行ログを取得
          const existing = issueLogsMap.get(log.ticketId);
          if (
            !existing ||
            new Date(log.actionAt) < new Date(existing.actionAt)
          ) {
            issueLogsMap.set(log.ticketId, log);
          }
        }
      });

      // チケット情報と結合してフォーマット
      return Array.from(issueLogsMap.values()).map((log) => {
        const ticket = tickets?.find((t) => t.id === log.ticketId);
        const now = new Date();
        const expireDate = new Date(log.expireDate || '');
        const daysUntilExpiry = Math.floor(
          (expireDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        );

        const status: TicketStatusType =
          expireDate < now
            ? 'expired'
            : daysUntilExpiry <= 10
            ? 'near_expiry'
            : 'active';

        return {
          id: `purchase-${log.id}`,
          purchaseDate: formatDateFromISO(log.actionAt),
          shopName:
            log.shopName || ticket?.shopName || DEFAULT_TICKET_VALUES.SHOP_NAME,
          ticketPlanName:
            log.ticketPlanName || DEFAULT_TICKET_VALUES.TICKET_PLAN_NAME,
          totalCount: log.prevRemaining || 0,
          unitSuffix: log.unitSuffix || DEFAULT_TICKET_VALUES.UNIT_SUFFIX,
          price: ticket?.price || 0,
          status,
          remainingCount: log.newRemaining || 0,
          expireDate: formatDate(log.expireDate),
        };
      });
    }, [usageLogs, tickets]);

  // usageLogsを表示用のフォーマットに変換
  const ticketUtilizeHistory: TicketUtilizeHistory[] = usageLogs.map((log) => {
    // actionAtをフォーマット
    const dateOfUse = formatDateFromISO(log.actionAt);

    // usageStatusの判定
    const usageStatus: UsageStatusType =
      log.changeType === 'issue'
        ? 'issued'
        : log.changeType === 'refund'
        ? 'refunded'
        : log.changeType === 'expire'
        ? 'expired'
        : 'used';

    return {
      id: log.id.toString(),
      dateOfUse,
      ticketTitle: log.ticketPlanName || DEFAULT_TICKET_VALUES.TICKET_PLAN_NAME,
      expireDate: formatDate(log.expireDate),
      usageStatus,
      changeAmount: log.changeAmount,
      unitType: log.unitSuffix || DEFAULT_TICKET_VALUES.UNIT_SUFFIX,
      newRemaining: log.newRemaining,
      prevRemaining: log.prevRemaining,
      ticketId: log.ticketId,
    };
  });

  // 最も近いアクティブな予約を取得（変更可能または変更不可の予約）
  const latestReservation = React.useMemo(() => {
    const now = new Date();

    const entries = reservations
      .filter(
        (reservation) =>
          reservation.status === 'available' ||
          reservation.status === 'notAllowedCustomerChange'
      )
      .map((reservation) => {
        const startDateTime = parseReservationDateTime(reservation);
        if (!startDateTime) {
          return null;
        }
        return { reservation, startDateTime };
      })
      .filter(
        (
          entry
        ): entry is {
          reservation: PublicWorkspaceCustomerReservation;
          startDateTime: Date;
        } => entry !== null
      )
      .sort((a, b) => a.startDateTime.getTime() - b.startDateTime.getTime());

    if (entries.length === 0) {
      return null;
    }

    const upcoming = entries.find(
      (entry) => entry.startDateTime.getTime() >= now.getTime()
    );

    return upcoming ? upcoming.reservation : null;
  }, [reservations]);

  // 予約編集ページへの遷移
  const handleEditReservation = (
    reservation: PublicWorkspaceCustomerReservation
  ) => {
    if (reservation.status !== 'available') {
      return; // キャンセル済みの予約は編集不可
    }

    // 現在のURLからreturnPathを取得
    const currentSearchParams = new URLSearchParams(window.location.search);
    const returnPath = currentSearchParams.get('returnPath');

    // 予約変更画面のURLを構築
    const reservationChangeUrl = `/a/${workspaceUid}/shops/${reservation.shopUid}/courses/${reservation.courseUid}`;

    // URLSearchParamsを使ってクエリパラメータを構築
    const params = new URLSearchParams();
    params.set('reservationKey', reservation.reservationKey);

    if (lineSessionKey) {
      params.set('_lineSessionKey', lineSessionKey);
    }

    // returnPathがある場合は、それも渡す
    if (returnPath) {
      params.set('returnPath', returnPath);
    }

    const resultUrl = `${reservationChangeUrl}?${params.toString()}`;
    window.location.href = resultUrl;
  };

  const tabList = [
    { id: 'reservation', label: '予約履歴' },
    { id: 'ticket-usage', label: 'チケット利用履歴' },
    { id: 'ticket-purchase', label: 'チケット購入履歴' },
  ];

  const createReservationMenuOptions = (
    reservation: PublicWorkspaceCustomerReservation,
    {
      canChange,
      canCancel,
    }: {
      canChange: boolean;
      canCancel: boolean;
    }
  ): MenuOption[] => {
    const menuOptions: MenuOption[] = [];

    if (canChange) {
      menuOptions.push({
        label: '予約を変更する',
        startIcon: <EventIcon />,
        onClick: () => {
          handleEditReservation(reservation);
        },
      });
    }

    if (canCancel) {
      menuOptions.push({
        label: '予約を取り消す',
        color: '#e63a1c',
        startIcon: <DeleteOutlineIcon />,
        onClick: () => {
          setSelectedReservation(reservation);
          setCancelDialogOpen(true);
          setOpenMenuId(null);
        },
      });
    }

    return menuOptions;
  };

  const styles = getStyles(primaryColor);

  // 次回予約の日付ラベル情報を取得
  const reservationReminderLabel = latestReservation
    ? getReservationReminderLabel(latestReservation.date)
    : { label: '', show: false, color: 'alert' as StatusColorType };

  // 予約キャンセル処理
  const handleCancelConfirm = async (reason?: string) => {
    if (!selectedReservation) return;

    // キャンセル理由が必須の場合は、理由が入力されていることを確認
    if (shopSetting && !shopSetting.hideCancelReason && !reason?.trim()) {
      return;
    }

    try {
      // 取り消し理由を送信
      await cancelReservation(
        workspaceUid,
        selectedReservation.shopUid,
        selectedReservation.courseUid,
        selectedReservation.reservationKey,
        reason || ''
      );
      setCancelDialogOpen(false);
      setCancelCompleteOpen(true);

      // 親コンポーネントに通知
      if (onReservationCanceled) {
        onReservationCanceled(selectedReservation.reservationKey);
      }
    } catch (error) {
      console.error('予約取り消しエラー:', error);
      alert('予約の取り消しに失敗しました。');
    }
  };

  // キャンセルダイアログを閉じる処理
  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setSelectedReservation(null);
  };

  // キャンセル完了ダイアログを閉じる処理
  const handleCancelCompleteClose = () => {
    setCancelCompleteOpen(false);
    setSelectedReservation(null);
  };

  return (
    <div css={styles.container}>
      {/* 次回予約日セクション */}
      <div css={styles.nextReservationSection}>
        {latestReservation ? (
          <>
            <div css={styles.nextReservationLabel}>次回予約</div>
            <div css={styles.nextReservationDateSection}>
              <span css={styles.nextReservationDate}>
                {formatDateWithWeekday(
                  latestReservation.date,
                  latestReservation.time
                )}
              </span>
              {reservationReminderLabel.show && (
                <StatusLabel
                  label={reservationReminderLabel.label}
                  color={reservationReminderLabel.color}
                />
              )}
            </div>
            <div css={styles.nextReservationDivider}></div>
            <div css={styles.nextReservationHeader}>
              <div>
                <div css={styles.nextReservationTitle}>
                  {latestReservation.shopName}
                </div>
                <div css={styles.nextReservationCourseName}>
                  {latestReservation.courseName}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div css={styles.noReservationContainer}>
            <div css={styles.noReservationText}>次回の予約はありません</div>
          </div>
        )}
      </div>

      {/* タブメニュー */}
      <div css={styles.tabContainer}>
        <div css={styles.activeTabBackground(activeTabIndex)} />
        {tabList.map((tab, index) => (
          <React.Fragment key={tab.id}>
            <button
              css={[styles.tab, activeTab === tab.id && styles.activeTab]}
              onClick={() => {
                setActiveTab(tab.id as TabType);
                setActiveTabIndex(index);
              }}
            >
              {tab.label}
            </button>
            {index < tabList.length - 1 && <div css={styles.tabDivider} />}
          </React.Fragment>
        ))}
      </div>

      {/* 予約履歴リスト */}
      {activeTab === 'reservation' && (
        <List list={reservations} emptyComment="予約履歴がありません。">
          {reservations.map((reservation, index) => {
            const canChangeReservation = reservation.status === 'available'; // 変更可能
            const cannotCustomerChange =
              reservation.status === 'notAllowedCustomerChange'; // 変更不可
            const isFutureReservation =
              cannotCustomerChange || canChangeReservation; // 変更不可と変更可能は未来の予約
            const canCancelReservation =
              reservation.canCancelReservationFromPublic;
            const reservationMenuOptions = createReservationMenuOptions(
              reservation,
              {
                canChange: canChangeReservation,
                canCancel: canCancelReservation,
              }
            );
            const hasReservationMenu = reservationMenuOptions.length > 0;

            // 予約日のリマインダーラベル情報を取得
            const reminderLabel = isFutureReservation
              ? getReservationReminderLabel(reservation.date)
              : { label: '', show: false, color: 'alert' as StatusColorType };

            return (
              <ListItem
                key={reservation.reservationKey}
                active={isFutureReservation}
                divider={index < reservations.length - 1}
                content={
                  <div css={styles.ticketPurchaseWrapper}>
                    <div css={styles.ticketPurchaseLeft}>
                      <div css={styles.ticketPurchaseDateRow}>
                        <div css={styles.ticketPurchaseDate}>
                          予約日:{' '}
                          {formatDateWithWeekday(
                            reservation.date,
                            reservation.time
                          )}
                        </div>
                        {reminderLabel.show && (
                          <StatusLabel
                            label={reminderLabel.label}
                            color={reminderLabel.color}
                          />
                        )}
                        {reservation.status === 'canceled' && (
                          <StatusLabel
                            label="キャンセル済み"
                            color="disabled"
                          />
                        )}
                        {reservation.status === 'expired' && (
                          <StatusLabel label="終了" color="disabled" />
                        )}
                        {reservation.status === 'notAllowedCustomerChange' && (
                          <StatusLabel label="変更不可" color="warning" />
                        )}
                      </div>
                      <div css={styles.ticketPurchaseHeader}>
                        <div
                          css={styles.reservationCourseName(
                            isFutureReservation
                          )}
                        >
                          {reservation.courseName}
                        </div>
                      </div>
                      <div css={styles.ticketPurchaseDetailsRow}>
                        <div css={styles.ticketPurchaseShop}>
                          <StorefrontIcon />
                          {reservation.shopName}
                        </div>
                      </div>
                    </div>
                    <div css={styles.ticketHistoryRight}>
                      {hasReservationMenu && (
                        <>
                          <button
                            type="button"
                            aria-label="メニューを開く"
                            onClick={() => {
                              setOpenMenuId(
                                openMenuId === reservation.reservationKey
                                  ? null
                                  : reservation.reservationKey
                              );
                            }}
                            css={styles.menuButton}
                          >
                            <MoreHorizIcon />
                          </button>
                          {openMenuId === reservation.reservationKey && (
                            <ModalMenu
                              menuOptions={reservationMenuOptions}
                              isMenuOpen={true}
                              setIsMenuOpen={(isOpen: boolean) => {
                                setOpenMenuId(
                                  isOpen ? reservation.reservationKey : null
                                );
                              }}
                              size="medium"
                              modalTitle="予約メニュー"
                              modalSubtitle={formatDateWithWeekday(
                                reservation.date,
                                reservation.time
                              )}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                }
              />
            );
          })}
        </List>
      )}

      {/* チケット利用履歴 */}
      {activeTab === 'ticket-usage' && (
        <List
          list={ticketUtilizeHistory}
          emptyComment="チケット利用履歴がありません。"
        >
          {ticketUtilizeHistory.map((history, index) => {
            // ステータス設定を事前に計算
            const statusConfig = getUsageStatusConfig(history.usageStatus);
            const isPositiveAmount =
              history.usageStatus === 'issued' ||
              history.usageStatus === 'refunded';
            const shopName =
              tickets?.find((t) => t.id === history.ticketId)?.shopName ||
              DEFAULT_TICKET_VALUES.SHOP_NAME;

            return (
              <ListItem
                key={history.id}
                active={true}
                divider={index < ticketUtilizeHistory.length - 1}
                content={
                  <div css={styles.ticketHistoryWrapper}>
                    <div css={styles.ticketPurchaseLeft}>
                      <div css={styles.ticketPurchaseDateRow}>
                        <div css={styles.ticketPurchaseDate}>
                          {history.dateOfUse}
                        </div>
                      </div>
                      <div css={styles.ticketPurchaseHeader}>
                        <div css={styles.ticketPurchaseName}>
                          {history.ticketTitle}
                        </div>
                      </div>
                      <div css={styles.ticketPurchaseDetailsRow}>
                        <div css={styles.ticketPurchaseShop}>
                          <StorefrontIcon />
                          {shopName}
                        </div>
                      </div>
                    </div>
                    <div css={styles.ticketHistoryRight}>
                      <StatusLabel
                        label={statusConfig.label}
                        color={statusConfig.color}
                      />
                      <div css={styles.ticketHistoryAmount(isPositiveAmount)}>
                        {isPositiveAmount ? '+' : '-'}
                        {Math.abs(history.changeAmount)}
                        <span css={styles.ticketHistoryUnit}>
                          {history.unitType}
                        </span>
                      </div>
                    </div>
                  </div>
                }
              />
            );
          })}
        </List>
      )}

      {/* チケット購入履歴 */}
      {activeTab === 'ticket-purchase' && (
        <List
          list={ticketPurchaseHistory}
          emptyComment="チケット購入履歴がありません。"
        >
          {ticketPurchaseHistory.map((history, index) => {
            const statusLabel = getTicketStatusLabel(history.status);

            return (
              <ListItem
                key={history.id}
                active={history.status !== 'expired'}
                divider={index < ticketPurchaseHistory.length - 1}
                content={
                  <div css={styles.ticketPurchaseWrapper}>
                    <div css={styles.ticketPurchaseLeft}>
                      <div css={styles.ticketPurchaseDateRow}>
                        <div css={styles.ticketPurchaseDate}>
                          購入日: {history.purchaseDate}
                        </div>
                        {statusLabel && (
                          <StatusLabel
                            label={statusLabel.label}
                            color={statusLabel.color}
                          />
                        )}
                      </div>
                      <div css={styles.ticketPurchaseHeader}>
                        <div css={styles.ticketPurchaseName}>
                          {history.ticketPlanName}
                        </div>
                      </div>
                      <div css={styles.ticketPurchaseDetailsRow}>
                        <div css={styles.ticketPurchaseShop}>
                          <StorefrontIcon />
                          {history.shopName}
                        </div>
                        <div css={styles.ticketPurchasePrice}>
                          {history.price > 0 ? (
                            <>
                              {history.price.toLocaleString()}円
                              <span css={styles.ticketPurchaseTaxLabel}>
                                (税込)
                              </span>
                            </>
                          ) : (
                            '-'
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                }
              />
            );
          })}
        </List>
      )}

      {/* 予約取り消しダイアログ */}
      {selectedReservation && (
        <>
          <CancelDialog
            isOpen={cancelDialogOpen}
            onClose={handleCancelDialogClose}
            onConfirm={handleCancelConfirm}
            shopName={selectedReservation.shopName}
            courseName={selectedReservation.courseName}
            date={selectedReservation.date}
            time={selectedReservation.time}
            showCancelReason={
              shopSetting ? !shopSetting.hideCancelReason : false
            }
          />
          <CancelComplete
            isOpen={cancelCompleteOpen}
            onClose={handleCancelCompleteClose}
            shopName={selectedReservation.shopName}
            courseName={selectedReservation.courseName}
            date={selectedReservation.date}
            time={selectedReservation.time}
          />
        </>
      )}
    </div>
  );
};

const textDisabled = '#999999';
const textLight = '#666666';
const textDefault = '#333333';
const borderThin = '#e0e0e0';
const borderThinner = '#f5f5f5ff';
const tabBackground = '#d5d5d5ff';

const getStyles = (primaryColor: string) => {
  // primaryColorの薄く彩度の高い背景色を生成
  const lightPrimaryBg = chroma(primaryColor).alpha(0.1).saturate(2).css();

  return {
    container: css`
      padding: 16px 0;
      min-height: 400px;
    `,
    nextReservationSection: css`
      background-color: white;
      padding: 16px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      border: 1px solid ${borderThinner};
      border-radius: 8px;
      margin: 0 16px 24px 16px;
    `,
    nextReservationTitle: css`
      font-size: 12px;
      color: ${textDefault};
      padding-top: 8px;
    `,
    nextReservationCourseName: css`
      font-size: 12px;
      line-height: 1.2;
      color: ${textDefault};
    `,
    nextReservationDateSection: css`
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    `,
    nextReservationLabel: css`
      display: inline-flex;
      align-items: center;
      background-color: ${lightPrimaryBg};
      color: ${primaryColor};
      font-size: 12px;
      font-weight: 600;
      margin-bottom: 8px;
      padding: 2px 10px;
      border-radius: 8px;
    `,
    nextReservationDate: css`
      font-size: 18px;
      font-weight: bold;
      color: ${textDefault};
    `,
    tabContainer: css`
      display: flex;
      margin: 16px 16px;
      padding: 2px;
      border-radius: 8px;
      border: 1px solid ${tabBackground};
      background-color: ${tabBackground};
      position: relative;
      overflow: hidden;
    `,
    tab: css`
      flex: 1;
      padding: 6px 8px;
      background-color: transparent;
      outline: none;
      border: none;
      font-size: 12px;
      letter-spacing: 0.5px;
      color: ${textLight};
      cursor: pointer;
      transition: color 0.3s ease;
      text-align: center;
      white-space: nowrap;
      min-width: 0;
      line-height: 1.5;
      border-radius: 4px;
      position: relative;
      z-index: 1;
      &:hover {
        opacity: 0.8;
      }
    `,
    activeTab: css`
      color: ${textDefault};
      font-weight: bold;
      outline: none;
      border: none;
      position: relative;
      z-index: 4;
      &:hover {
        opacity: 1;
      }
    `,
    activeTabBackground: (tabIndex: number) => css`
      position: absolute;
      top: 2px;
      left: 2px;
      width: calc(100% / 3 - 1.1px);
      height: 30px;
      background-color: #ffffffff;
      border-radius: 8px;
      transform: translateX(${tabIndex * 100}%);
      transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      z-index: 2;
    `,
    tabDivider: css`
      border-left: 1px solid ${borderThin};
      margin: 8px 0;
      position: relative;
      z-index: 0;
    `,
    reservationCourseName: (isFutureReservation: boolean) => css`
      font-size: 16px;
      font-weight: 700;
      line-height: 1.7;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      max-width: 280px;
      color: ${isFutureReservation ? textDefault : textDisabled};
    `,
    nextReservationDivider: css`
      border-bottom: 1px solid ${borderThin};
    `,
    nextReservationHeader: css`
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    `,
    // チケット利用履歴のスタイル
    ticketHistoryWrapper: css`
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    `,
    ticketHistoryRight: css`
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 4px;
    `,
    ticketHistoryAmount: (isPositive: boolean) => css`
      font-size: 16px;
      font-weight: bold;
      color: ${isPositive ? '#e63a1c' : '#4caf50'};
    `,
    ticketHistoryUnit: css`
      font-size: 12px;
      font-weight: normal;
      margin-left: 2px;
    `,
    // チケット購入履歴のスタイル
    ticketPurchaseWrapper: css`
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    `,
    ticketPurchaseLeft: css`
      display: flex;
      flex-direction: column;
      flex: 1;
    `,
    ticketPurchaseDateRow: css`
      display: flex;
      align-items: center;
      gap: 8px;
      padding-bottom: 2px;
    `,
    ticketPurchaseHeader: css`
      display: flex;
      align-items: center;
    `,
    ticketPurchaseName: css`
      font-size: 16px;
      font-weight: 700;
      color: ${textDefault};
      line-height: 1.7;
    `,
    ticketPurchaseDetailsRow: css`
      display: flex;
      justify-content: space-between;
      align-items: center;
    `,
    ticketPurchaseDate: css`
      font-size: 12px;
      color: ${textLight};
      line-height: 1.2;
    `,
    ticketPurchaseShop: css`
      font-size: 10px;
      color: ${textLight};
      line-height: 1;
      display: flex;
      align-items: center;
      gap: 2px;
      svg {
        font-size: 16px;
      }
    `,
    ticketPurchasePrice: css`
      font-size: 14px;
      color: ${textLight};
      white-space: nowrap;
    `,
    ticketPurchaseTaxLabel: css`
      font-size: 10px;
      color: ${textLight};
      margin-left: 2px;
    `,
    noReservationContainer: css`
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 80px;
    `,
    noReservationText: css`
      font-size: 14px;
      color: ${textLight};
    `,
    menuButton: css`
      background: #e0e0e0;
      border: none;
      cursor: pointer;
      padding: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: background-color 0.2s;
      color: #666;
      &:hover {
        background-color: #d0d0d0;
      }
    `,
  };
};
